export const ACTIVITY_LABELS = {
  call: "On call",
  meeting: "In meeting",
  rdp: "RDP",
  vdi: "VDI",
  shell: "Shell",
  media: "Watching media",
  browsing: "Browsing",
  office: "Office",
  app: "In an app",
  remote: "Remote",
  idle: "Idle",
  break: "On break",
  leave: "On leave",
  working: "Working",
  offline: "Offline",
  suspicious: "Suspicious input",
}

export const LIVE_ACTIVITY_FILTERS = [
  { key: "all", label: "All live" },
  { key: "suspicious", label: "Suspicious input" },
  { key: "call", label: "On call" },
  { key: "meeting", label: "Meeting" },
  { key: "media", label: "YouTube / media" },
  { key: "browsing", label: "Browsing" },
  { key: "office", label: "Office" },
  { key: "app", label: "Any app" },
  { key: "shell", label: "Shell" },
  { key: "rdp", label: "RDP" },
  { key: "vdi", label: "VDI" },
  { key: "idle", label: "Idle" },
]

export function activityLabel(value) {
  if (!value) return "—"
  return ACTIVITY_LABELS[value] || value
}

export function activityTagLabel(type, record = {}) {
  if (
    ["app", "office", "shell"].includes(type) &&
    record.appName &&
    !["Lock screen", "Unknown"].includes(record.appName)
  ) {
    return record.appName
  }
  return activityLabel(type)
}

export function activityPlatformLabel(record = {}) {
  if (!["call", "meeting"].includes(record.primaryActivity)) return ""
  const types = record.activityTypes || []
  if (!types.includes("call") && !types.includes("meeting") && record.primaryActivity) {
    return ""
  }
  const app = String(record.appName || "").trim()
  if (!app) return ""
  if (["Lock screen", "Unknown"].includes(app)) return ""
  return app
}

export const CATEGORY_COLORS = {
  call: "#dc2626",
  meeting: "#7c3aed",
  media: "#db2777",
  browsing: "#2563eb",
  office: "#059669",
  app: "#0ea5e9",
  shell: "#334155",
  rdp: "#d97706",
  vdi: "#ea580c",
  remote: "#ca8a04",
  idle: "#94a3b8",
  break: "#f59e0b",
  leave: "#64748b",
  working: "#475569",
  offline: "#cbd5e1",
  suspicious: "#b91c1c",
}

export function activityTagColor(type) {
  if (type === "call" || type === "suspicious") return "red"
  if (type === "meeting") return "purple"
  if (type === "media") return "magenta"
  if (type === "idle" || type === "break") return "gold"
  if (type === "leave" || type === "offline") return "default"
  if (["app", "office", "shell", "browsing", "rdp", "vdi", "remote", "working"].includes(type)) {
    return "processing"
  }
  return undefined
}

/** Hex accents for live KPI chips / activity dots (matches CATEGORY_COLORS). */
export function activityAccent(type) {
  if (type === "all") return "#2563eb"
  return CATEGORY_COLORS[type] || "#64748b"
}

export function isSuspiciousInput(record = {}) {
  const status = String(
    record.inputIntegrityStatus || record.inputIntegrity?.status || ""
  ).toLowerCase()
  return status === "suspicious"
}

export function suspiciousInputTitle(record = {}) {
  const integrity = record.inputIntegrity || {}
  const parts = [
    "Looks like fake activity (low input diversity)",
    integrity.reason || "low-input-diversity",
    integrity.score != null ? `score ${integrity.score}` : "",
    integrity.mouseMovePx != null ? `mouse ${integrity.mouseMovePx}px` : "",
    integrity.fgChanges != null ? `window changes ${integrity.fgChanges}` : "",
    Array.isArray(integrity.signals) && integrity.signals.length
      ? integrity.signals.join(", ")
      : "",
  ].filter(Boolean)
  return parts.join(" · ")
}

export function visibleActivityTypes(types = [], primary) {
  const list = (types.length ? types : [primary]).filter(Boolean)
  const specific = list.filter((type) => !["working", "offline"].includes(type))
  const core = specific.length ? specific : list
  if (core.includes("call") || core.includes("meeting")) {
    return core.filter((type) => !["app", "browsing", "office"].includes(type))
  }
  return core
}

export function formatDuration(seconds) {
  if (!seconds || seconds === 0) return "0s"
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${secs}s`
  return `${secs}s`
}

export function hoursFromSeconds(seconds) {
  return Math.round(((Number(seconds) || 0) / 3600) * 100) / 100
}

export function formatHours(seconds) {
  return `${hoursFromSeconds(seconds).toFixed(2)}h`
}

export function matchesLiveFilter(record, activity) {
  if (!activity || activity === "all") return true
  if (activity === "suspicious") return isSuspiciousInput(record)
  const wanted =
    activity === "app" ? ["app", "office", "shell"] : [activity]
  const types = record?.activityTypes || []
  return (
    wanted.includes(record?.primaryActivity) ||
    wanted.some((type) => types.includes(type))
  )
}

export function formatPercent(part, whole) {
  const total = Number(whole)
  const value = Number(part)
  if (!total || !Number.isFinite(value)) return "0%"
  return `${Math.round((value / total) * 100)}%`
}

export function commPeer(record = {}) {
  return String(
    record.activitySubject ||
      record.subject ||
      record.currentActivity?.subject ||
      ""
  ).trim()
}

export function commStartedAt(record = {}) {
  return (
    record.activityStartedAt ||
    record.startedAt ||
    record.currentActivity?.startedAt ||
    null
  )
}

export function formatElapsed(start) {
  if (!start) return ""
  const at = new Date(start)
  if (Number.isNaN(at.valueOf())) return ""
  const seconds = Math.max(0, Math.floor((Date.now() - at.getTime()) / 1000))
  if (seconds < 20) return "just started"
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

export function formatCommDoing(record = {}) {
  const kind = String(record.primaryActivity || record.currentActivity?.primary || "")
  if (kind !== "call" && kind !== "meeting") return null
  const peer = commPeer(record)
  const app = String(record.appName || record.currentActivity?.appName || "").trim()
  const elapsed = formatElapsed(commStartedAt(record))
  const extras = [elapsed, app].filter(Boolean)
  if (kind === "call") {
    return {
      main: peer ? `On call with ${peer}` : "On call",
      extras,
    }
  }
  return {
    main: peer ? `In meeting · ${peer}` : "In meeting",
    extras,
  }
}

export function formatIdle(seconds) {
  const value = Number(seconds)
  if (!Number.isFinite(value) || value <= 0) return "Active"
  if (value < 60) return `${Math.floor(value)}s idle`
  if (value < 3600) return `${Math.floor(value / 60)}m idle`
  return `${Math.floor(value / 3600)}h ${Math.floor((value % 3600) / 60)}m idle`
}
